import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Check, Clock, MapPin, MessageCircle, ShieldCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FLEET, estimateFleetPrice } from "@/lib/fleet";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import { formatMAD } from "@/lib/pricing";
import { useFormatPrice } from "@/lib/currency";

interface CityProfile {
  slug: string;
  city: string;
  airport: string;
  airportCode: string;
  hero: string;
  intro: string;
  toCenterKm: number;
  popular: Array<{ to: string; km: number }>;
  highlights: string[];
}

const CITIES: Record<string, CityProfile> = {
  casablanca: {
    slug: "casablanca", city: "Casablanca", airport: "Mohammed V International", airportCode: "CMN",
    hero: "Private transfers from Casablanca Airport (CMN)",
    intro: "Skip the taxi queue at Mohammed V. Your English-speaking driver tracks your flight and meets you in arrivals with a name sign — for a fixed, all-inclusive price.",
    toCenterKm: 30,
    popular: [
      { to: "Casablanca city center", km: 30 },
      { to: "Rabat", km: 95 },
      { to: "Marrakech", km: 240 },
      { to: "El Jadida", km: 100 },
    ],
    highlights: ["Flight tracking included", "60 min free waiting at CMN", "Meet & greet in arrivals"],
  },
  marrakech: {
    slug: "marrakech", city: "Marrakech", airport: "Marrakech Menara", airportCode: "RAK",
    hero: "Private transfers from Marrakech Airport (RAK)",
    intro: "Land at Menara and step straight into a clean, air-conditioned car with bottled water — door-to-door to your riad inside the medina, no haggling, no surprises.",
    toCenterKm: 6,
    popular: [
      { to: "Marrakech Medina / Riad", km: 6 },
      { to: "Essaouira", km: 190 },
      { to: "Ouarzazate", km: 200 },
      { to: "Casablanca", km: 240 },
      { to: "Merzouga (desert)", km: 560 },
    ],
    highlights: ["Riads inside the medina — drop-off as close as cars are allowed", "Bottled water on board", "Child seats on request"],
  },
  fez: {
    slug: "fez", city: "Fez", airport: "Fès–Saïs", airportCode: "FEZ",
    hero: "Private transfers from Fez Airport (FEZ)",
    intro: "Quiet, comfortable rides from Fès–Saïs to the medina, the new city, or onward to Chefchaouen and the Rif mountains.",
    toCenterKm: 15,
    popular: [
      { to: "Fez Medina", km: 15 },
      { to: "Chefchaouen", km: 200 },
      { to: "Meknes", km: 60 },
      { to: "Volubilis", km: 90 },
    ],
    highlights: ["Local drivers who know the medina gates", "Quiet, premium-class options", "Long-distance one-way to Chefchaouen"],
  },
  tangier: {
    slug: "tangier", city: "Tangier", airport: "Ibn Battouta", airportCode: "TNG",
    hero: "Private transfers from Tangier Airport (TNG)",
    intro: "From the Strait of Gibraltar to your hotel, riad, or onward to Chefchaouen and Tetouan — booked in 60 seconds on WhatsApp.",
    toCenterKm: 15,
    popular: [
      { to: "Tangier city", km: 15 },
      { to: "Tangier MED port", km: 45 },
      { to: "Chefchaouen", km: 115 },
      { to: "Tetouan", km: 65 },
      { to: "Asilah", km: 45 },
    ],
    highlights: ["Ferry-port transfers (Tanger MED)", "Cross-Rif routes to Chefchaouen", "Premium and family vans"],
  },
  agadir: {
    slug: "agadir", city: "Agadir", airport: "Al Massira", airportCode: "AGA",
    hero: "Private transfers from Agadir Airport (AGA)",
    intro: "Direct rides from Al Massira to Agadir beachfront, Taghazout surf town, or Paradise Valley — fixed price, no detours.",
    toCenterKm: 40,
    popular: [
      { to: "Agadir city / beach", km: 40 },
      { to: "Taghazout", km: 60 },
      { to: "Essaouira", km: 175 },
      { to: "Paradise Valley", km: 80 },
    ],
    highlights: ["Surf-friendly vans (board storage)", "Family monospaces with extra luggage", "Night arrivals welcomed"],
  },
  rabat: {
    slug: "rabat", city: "Rabat", airport: "Rabat–Salé", airportCode: "RBA",
    hero: "Private transfers from Rabat Airport (RBA)",
    intro: "Business-ready transfers from Rabat–Salé to the diplomatic quarter, ministries, or onward to Casablanca.",
    toCenterKm: 12,
    popular: [
      { to: "Rabat city", km: 12 },
      { to: "Casablanca", km: 95 },
      { to: "Kenitra", km: 50 },
      { to: "Meknes", km: 140 },
    ],
    highlights: ["Premium sedans for business travel", "Discreet, on-time chauffeurs", "Corporate invoicing available"],
  },
  essaouira: {
    slug: "essaouira", city: "Essaouira", airport: "Mogador", airportCode: "ESU",
    hero: "Private transfers from Essaouira",
    intro: "From Mogador airport or Marrakech, a stress-free coastal ride to the medina of Essaouira.",
    toCenterKm: 17,
    popular: [
      { to: "Essaouira Medina", km: 17 },
      { to: "Marrakech", km: 190 },
      { to: "Agadir", km: 175 },
      { to: "Sidi Kaouki", km: 25 },
    ],
    highlights: ["Long-distance comfort vehicles", "Stops on request (argan cooperatives, beaches)", "Luggage-friendly vans"],
  },
  chefchaouen: {
    slug: "chefchaouen", city: "Chefchaouen", airport: "nearest: Tangier (TNG)", airportCode: "TNG",
    hero: "Private transfers to the Blue City",
    intro: "The most scenic ride in Morocco — from Tangier, Fez, or Casablanca to the blue-washed alleys of Chefchaouen.",
    toCenterKm: 115,
    popular: [
      { to: "from Tangier (TNG)", km: 115 },
      { to: "from Fez (FEZ)", km: 200 },
      { to: "from Casablanca", km: 340 },
      { to: "Akchour waterfalls", km: 30 },
    ],
    highlights: ["Mountain-experienced drivers", "Photo stops on the Rif road", "Hotel-to-hotel door service"],
  },
  ouarzazate: {
    slug: "ouarzazate", city: "Ouarzazate", airport: "Ouarzazate", airportCode: "OZZ",
    hero: "Private transfers — Gateway to the Sahara",
    intro: "From Marrakech across the Atlas (Tizi n'Tichka), or direct from Ouarzazate airport to Aït Ben Haddou and the desert.",
    toCenterKm: 5,
    popular: [
      { to: "Aït Ben Haddou", km: 30 },
      { to: "from Marrakech", km: 200 },
      { to: "Skoura oasis", km: 45 },
      { to: "Zagora", km: 165 },
    ],
    highlights: ["4x4 capable on request", "Atlas-mountain experienced drivers", "Multi-day Sahara packages"],
  },
  merzouga: {
    slug: "merzouga", city: "Merzouga", airport: "nearest: Errachidia", airportCode: "ERH",
    hero: "Private transfers to the Erg Chebbi dunes",
    intro: "Long-distance transfers from Marrakech, Fez, or Errachidia to the golden dunes of Merzouga — with hotel coordination at arrival.",
    toCenterKm: 130,
    popular: [
      { to: "from Marrakech", km: 560 },
      { to: "from Fez", km: 470 },
      { to: "from Errachidia (ERH)", km: 130 },
      { to: "from Ouarzazate", km: 360 },
    ],
    highlights: ["Multi-day desert itineraries", "Coordinated camp drop-off", "Premium long-distance comfort"],
  },
  meknes: {
    slug: "meknes", city: "Meknes", airport: "nearest: Fez (FEZ)", airportCode: "FEZ",
    hero: "Private transfers to Meknes & Volubilis",
    intro: "Imperial-city transfers from Fez airport to Meknes, Volubilis, and Moulay Idriss.",
    toCenterKm: 60,
    popular: [
      { to: "from Fez airport", km: 60 },
      { to: "Volubilis", km: 30 },
      { to: "Moulay Idriss", km: 30 },
      { to: "Ifrane", km: 65 },
    ],
    highlights: ["Multi-stop day tours", "English-speaking guides on request", "Imperial-cities loops"],
  },
  tetouan: {
    slug: "tetouan", city: "Tetouan", airport: "Saniat R'mel", airportCode: "TTU",
    hero: "Private transfers in the north",
    intro: "From Tangier airport or Tanger MED port to Tetouan, Martil, and Cabo Negro beach resorts.",
    toCenterKm: 5,
    popular: [
      { to: "from Tangier (TNG)", km: 65 },
      { to: "from Tanger MED port", km: 50 },
      { to: "Martil / Cabo Negro", km: 15 },
      { to: "Chefchaouen", km: 65 },
    ],
    highlights: ["Ferry-port pickups", "Beach-resort hotels covered", "Premium minivans for families"],
  },
};

export const Route = createFileRoute("/transfer/$slug")({
  loader: ({ params }): { city: CityProfile } => {
    const city = CITIES[params.slug];
    if (!city) throw notFound();
    return { city };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.city;
    if (!c) return { meta: [{ title: "Transfer not found — Bacha Way Travel" }] };
    const url = `https://bachawaytravel.com/transfer/${c.slug}`;
    const price = formatMAD(estimateFleetPrice(FLEET[0], c.toCenterKm));
    return {
      meta: [
        { title: `${c.city} Private Airport Transfers — Fixed Prices | Bacha Way` },
        { name: "description", content: `Private airport transfers in ${c.city} (${c.airportCode}). Fixed prices from ${price}, English-speaking drivers, 60 min free waiting, meet & greet. Book in 60 seconds.` },
        { property: "og:title", content: `${c.city} private transfers — Bacha Way Travel` },
        { property: "og:description", content: `${c.hero}. Fixed prices from ${price}.` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Private airport transfer",
          provider: { "@type": "Organization", name: "Bacha Way Travel", url: "https://bachawaytravel.com" },
          areaServed: { "@type": "City", name: c.city, address: { "@type": "PostalAddress", addressCountry: "MA" } },
          name: `${c.city} private airport transfer`,
          description: c.hero,
          url,
        }),
      }],
    };
  },
  notFoundComponent: NotFoundCity,
  errorComponent: ErrorCity,
  component: CityPage,
});

function NotFoundCity() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-2xl px-6 py-32 text-center">
      <h1 className="font-display text-4xl">{t("transfer.notFoundTitle")}</h1>
      <p className="mt-4 text-muted-foreground">{t("transfer.notFoundLead")}</p>
      <Link to="/booking" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm text-primary-foreground">
        {t("common.bookCta")} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function ErrorCity() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-32 text-center">
      <h1 className="font-display text-4xl">Something went wrong</h1>
      <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm text-primary-foreground">Back home</Link>
    </div>
  );
}

function CityPage() {
  const { city } = Route.useLoaderData() as { city: CityProfile };
  const km = city.toCenterKm || city.popular[0]?.km || 30;
  const fmt = useFormatPrice();
  const { t, i18n } = useTranslation();

  // Look up localized city content; fall back to source strings.
  const cKey = `cities.${city.slug}`;
  const heroT = i18n.exists(`${cKey}.hero`) ? t(`${cKey}.hero`) : city.hero;
  const introT = i18n.exists(`${cKey}.intro`) ? t(`${cKey}.intro`) : city.intro;
  const highlightsT = (i18n.getResource(i18n.language, "translation", `${cKey}.hl`) as string[] | undefined) ?? city.highlights;
  const toMap = (i18n.getResource(i18n.language, "translation", `${cKey}.to`) as Record<string, string> | undefined) ?? {};
  const localizeTo = (s: string) => toMap[s] ?? s;

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <Link to="/routes" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-primary hover:underline">
            <MapPin className="h-3 w-3" /> {t("transfer.moroccoTransfers")}
          </Link>
          <h1 className="mt-4 font-display text-4xl md:text-6xl max-w-3xl">{heroT}.</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{introT}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/booking" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:opacity-90">
              {t("common.bookCta")} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={buildWhatsAppUrl(`Hello Bacha Way — I'd like a private transfer in ${city.city}.`)}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white hover:opacity-90"
            >
              <MessageCircle className="h-4 w-4" /> {t("common.quoteWa")}
            </a>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3 max-w-3xl">
            {highlightsT.map((h, i) => (
              <div key={i} className="rounded-xl border border-border bg-card/60 p-4 text-sm flex items-start gap-2">
                <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {h}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-primary">{t("transfer.pricingFor", { city: city.city })}</div>
        <h2 className="mt-3 font-display text-3xl md:text-4xl">{t("transfer.fromAirport", { km })}</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground text-sm">
          {t("transfer.sampleLead", { km })}
        </p>

        <div className="mt-10 space-y-3">
          {FLEET.map((v) => {
            const price = estimateFleetPrice(v, km);
            return (
              <div key={v.id} className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col md:flex-row md:items-center">
                <div className="md:w-56 shrink-0 aspect-[16/10] md:aspect-[4/3] bg-secondary/40">
                  <img src={v.image} alt={`${v.name} class private transfer vehicle — ${v.type}`} loading="lazy" className="h-full w-full object-contain p-3" />
                </div>
                <div className="flex-1 p-5 md:p-6">
                  <h3 className="font-display text-xl md:text-2xl">{v.name} — private driver {city.city}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {v.pax} {t("common.perPax")}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t("transfer.freeWait")}</span>
                    <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {t("transfer.insured")}</span>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{v.description}</p>
                </div>
                <div className="px-6 pb-6 md:pb-6 md:pr-8 md:text-right">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{t("transfer.from")}</div>
                  <div className="font-display text-3xl text-primary">{fmt(price)}</div>
                  <Link to="/booking" className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary px-4 py-2 text-xs text-primary-foreground hover:opacity-90">
                    {t("transfer.bookThis")} <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/20">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">{t("transfer.popularFrom", { city: city.city })}</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl">{t("transfer.oneWay")}</h2>

          <div className="mt-10 grid gap-3 md:grid-cols-2">
            {city.popular.map((r) => {
              const p = estimateFleetPrice(FLEET[0], r.km);
              return (
                <Link key={r.to} to="/booking" className="group flex items-center justify-between rounded-xl border border-border bg-card p-5 hover:border-primary/40">
                  <div>
                    <h4 className="text-foreground font-medium">{city.city} → {localizeTo(r.to)}</h4>
                    <div className="text-xs text-muted-foreground mt-1">{r.km} km · ~{Math.round(r.km / 70 * 60)} min</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{t("transfer.from")}</div>
                    <div className="font-display text-xl text-primary">{p ? fmt(p) : "—"}</div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link to="/booking" className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-medium text-primary-foreground hover:opacity-90">
              {t("transfer.bookThis")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
